import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Eye, Pencil, ArrowLeft, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  isImpersonating, getBackupAdminName, getImpersonationExpiry,
  endImpersonation, onImpersonationChange,
} from "@/lib/impersonation";

const WARN_AT_MS = 5 * 60 * 1000; // 5 min

/**
 * Sticky yellow top banner. Live countdown to token expiry, one-time warning
 * toast at 5 min remaining, auto-force-exit when the imp JWT dies.
 */
export default function ImpersonationBanner() {
  const { user } = useAuth();
  const loc = useLocation();
  const [tick, setTick] = useState(0);
  const [now, setNow] = useState(Date.now());
  const warnedRef = useRef(false);

  useEffect(() => onImpersonationChange(() => setTick((t) => t + 1)), []);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const active = isImpersonating();

  useEffect(() => {
    if (!active) { warnedRef.current = false; return; }
    const exp = getImpersonationExpiry();
    if (!exp) return;
    const remaining = exp - now;
    if (remaining <= 0) {
      endImpersonation();
      setTimeout(() => { window.location.replace("/admin"); }, 30);
      toast.info("Impersonation session expired — back to Admin");
      return;
    }
    if (remaining <= WARN_AT_MS && !warnedRef.current) {
      warnedRef.current = true;
      toast.warning("Impersonation ends in 5 minutes", { duration: 8000 });
    }
  }, [now, active]);

  if (!active) return null;
  if (loc.pathname === "/login" || loc.pathname === "/") return null;
  if (!user) return null;

  const adminName = getBackupAdminName() || user?._impersonation?.admin_name || "Admin";
  const targetName = user?.name || "Staff";
  const targetPhone = user?.phone || "";
  const exp = getImpersonationExpiry();
  const remaining = Math.max(0, exp - now);
  const mm = Math.floor(remaining / 60000);
  const ss = Math.floor((remaining % 60000) / 1000);
  const isLow = remaining <= WARN_AT_MS && remaining > 0;

  const backToAdmin = () => {
    endImpersonation();
    setTimeout(() => { window.location.replace("/admin"); }, 30);
  };
  const editProfile = () => {
    endImpersonation();
    setTimeout(() => { window.location.replace(`/admin?edit_user=${encodeURIComponent(user.id)}`); }, 30);
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-[9999999] w-full bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 text-slate-950 border-b-2 border-amber-600 shadow-[0_4px_20px_-4px_rgba(217,119,6,0.6)]"
      key={tick}
    >
      <div className="max-w-[1600px] mx-auto px-4 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 sm:pr-[360px] xl:pr-[380px]">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-full bg-slate-900/10 flex items-center justify-center shrink-0">
            <Eye className="w-4 h-4"/>
          </div>
          <div className="text-xs sm:text-sm leading-tight min-w-0">
            <div className="font-bold truncate" data-testid="impersonation-target">
              You are viewing as <span className="uppercase tracking-wide">{targetName}</span>
              {targetPhone && <span className="font-mono ml-1">({targetPhone})</span>}
            </div>
            <div className="text-[10px] sm:text-xs opacity-80 flex items-center gap-2 flex-wrap">
              <span>Impersonated by <span className="font-semibold">{adminName}</span></span>
              <span
                data-testid="impersonation-countdown"
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono font-bold ${
                  isLow ? "bg-red-600 text-white animate-pulse" : "bg-slate-900/20 text-slate-900"
                }`}>
                <Clock className="w-3 h-3"/> {String(mm).padStart(2,"0")}:{String(ss).padStart(2,"0")}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:ml-auto shrink-0">
          <button
            onClick={editProfile}
            data-testid="impersonation-edit-profile"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-amber-200 hover:bg-slate-800 px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors">
            <Pencil className="w-3.5 h-3.5"/> Edit Full Profile
          </button>
          <button
            onClick={backToAdmin}
            data-testid="impersonation-back-to-admin"
            className="inline-flex items-center gap-1.5 rounded-full bg-white text-slate-900 hover:bg-slate-100 px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors border border-slate-900/20">
            <ArrowLeft className="w-3.5 h-3.5"/> Back to Admin
          </button>
        </div>
      </div>
    </div>
  );
}
